import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {Choir, Project} from "../../utils/types";
import ProjectService from "./project_services";
import {CreateProjectPayload, ProjectFilters, ProjectsState, UpdateProjectPayload} from "./types";
import ChoirService from "../choirs/choir_services";

const initialState: ProjectsState = {
    projects: [],
    ownerProjects: [],
    project: null,
    projectRoles: [],
    projectRoleInvites: [],
    projectApplications: [],
    projectApplication: null,
    myApplications: [],
    projectMembers: [],
    projectAgreements: [],
    projectAgreement: null,
    myInvites: [],
    workspace: null,
    ownerChoirs: [],
    loading: false,
    error: null,
};

export const fetchProjects = createAsyncThunk(
    "projects/fetchProjects",
    async (query: ProjectFilters, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjects(query);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchOwnerProjects = createAsyncThunk(
    "projects/fetchOwnerProjects",
    async (_: void, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getOwnerProjects();
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProject = createAsyncThunk(
    "projects/fetchProject",
    async (id: string, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProject(id);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createProject = createAsyncThunk(
    "projects/createProject",
    async (payload: CreateProjectPayload, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.createProject(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateProject = createAsyncThunk(
    "projects/updateProject",
    async ({projectId, payload}: {projectId: string, payload: UpdateProjectPayload}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.updateProject(projectId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectRoles = createAsyncThunk(
    "projects/fetchProjectRoles",
    async (projectId: string, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjectRoles(projectId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createProjectRole = createAsyncThunk(
    "projects/createProjectRole",
    async ({projectId, payload}: {projectId: string, payload: any}, {rejectWithValue}): Promise<any> => {
        try {
            const response = await ProjectService.createProjectRole(projectId, payload);
            return {
                roles: response?.data || [],
                message: response?.message || null,
                warning: response?.warning || null,
            };
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateProjectRole = createAsyncThunk(
    "projects/updateProjectRole",
    async ({projectId, projectRoleId, payload}: {projectId: string, projectRoleId: string | number, payload: any}, {rejectWithValue}): Promise<any> => {
        try {
            const response = await ProjectService.updateProjectRole(projectId, projectRoleId, payload);
            return {
                roles: response?.data || [],
                message: response?.message || null,
                warning: response?.warning || null,
            };
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const deleteProjectRole = createAsyncThunk(
    "projects/deleteProjectRole",
    async ({projectId, projectRoleId}: {projectId: string, projectRoleId: string | number}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.deleteProjectRole(projectId, projectRoleId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const applyToProjectRole = createAsyncThunk(
    "projects/applyToProjectRole",
    async (payload: any, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.createProjectApplication(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectApplications = createAsyncThunk(
    "projects/fetchProjectApplications",
    async ({projectId, status}: {projectId: string, status?: string}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjectApplications(projectId, status ? {status} : undefined);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectApplication = createAsyncThunk(
    "projects/fetchProjectApplication",
    async ({projectId, applicationId}: {projectId: string, applicationId: string}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjectApplication(projectId, applicationId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchMyProjectApplications = createAsyncThunk(
    "projects/fetchMyProjectApplications",
    async (_: void, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getMyProjectApplications();
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const reviewProjectApplication = createAsyncThunk(
    "projects/reviewProjectApplication",
    async ({projectId, applicationId, payload}: any, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.reviewProjectApplication(projectId, applicationId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectMembers = createAsyncThunk(
    "projects/fetchProjectMembers",
    async (projectId: string, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjectMembers(projectId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectAgreements = createAsyncThunk(
    "projects/fetchProjectAgreements",
    async (projectId: string, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getProjectAgreements(projectId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createProjectAgreement = createAsyncThunk(
    "projects/createProjectAgreement",
    async (
        {projectId, payload}: {projectId: string; payload: {project_role_id: string | number; project_member_id: string | number}},
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.createProjectAgreement(projectId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectAgreement = createAsyncThunk(
    "projects/fetchProjectAgreement",
    async (agreementId: string | number, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getAgreement(agreementId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fundProjectAgreement = createAsyncThunk(
    "projects/fundProjectAgreement",
    async (
        {agreementId, payload}: {agreementId: string | number; payload?: {provider?: 'paystack' | 'stripe'}},
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.fundAgreement(agreementId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fundProjectAgreements = createAsyncThunk(
    "projects/fundProjectAgreements",
    async (
        {
            projectId,
            payload,
        }: {
            projectId: string;
            payload?: {provider?: 'paystack' | 'stripe'; agreement_ids?: Array<string | number>};
        },
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.fundProject(projectId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const releaseProjectAgreement = createAsyncThunk(
    "projects/releaseProjectAgreement",
    async (
        {agreementId, payload}: {agreementId: string | number; payload?: {provider_reference?: string}},
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.releaseAgreement(agreementId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const refundProjectAgreement = createAsyncThunk(
    "projects/refundProjectAgreement",
    async (agreementId: string | number, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.refundAgreement(agreementId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const requestProjectAgreementWithdrawal = createAsyncThunk(
    "projects/requestProjectAgreementWithdrawal",
    async (agreementId: string | number, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.requestAgreementWithdrawal(agreementId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createProjectRoleInvite = createAsyncThunk(
    "projects/createProjectRoleInvite",
    async ({projectId, projectRoleId, invited_email}: {projectId: string, projectRoleId: string | number, invited_email: string}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.createRoleInvite(projectId, projectRoleId, {invited_email});
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchProjectRoleInvites = createAsyncThunk(
    "projects/fetchProjectRoleInvites",
    async (
        {projectId, status, project_role_id}: {projectId: string, status?: string, project_role_id?: string},
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.getProjectRoleInvites(projectId, {
                ...(status ? {status} : {}),
                ...(project_role_id ? {project_role_id} : {}),
            });
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateProjectRoleInvite = createAsyncThunk(
    "projects/updateProjectRoleInvite",
    async (
        {
            projectId,
            projectRoleId,
            inviteId,
            payload,
        }: {
            projectId: string;
            projectRoleId: string | number;
            inviteId: string | number;
            payload: {action: 'revoke' | 'resend'; note?: string; expires_at?: string};
        },
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.updateProjectRoleInvite(projectId, projectRoleId, inviteId, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchMyProjectInvites = createAsyncThunk(
    "projects/fetchMyProjectInvites",
    async (_: void, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.getMyRoleInvites();
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const linkProjectChoirWorkspace = createAsyncThunk(
    "projects/linkProjectChoirWorkspace",
    async ({projectId, choirId}: {projectId: string, choirId: string | number}, {rejectWithValue}): Promise<any> => {
        try {
            return await ProjectService.linkChoirWorkspace(projectId, {choir_id: choirId});
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

export const syncProjectChoirWorkspace = createAsyncThunk(
    "projects/syncProjectChoirWorkspace",
    async (
        {projectId, memberIds}: {projectId: string; memberIds?: Array<string | number>},
        {rejectWithValue}
    ): Promise<any> => {
        try {
            return await ProjectService.syncChoirWorkspace(
                projectId,
                memberIds && memberIds.length > 0 ? {member_ids: memberIds} : undefined
            );
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

export const fetchOwnerChoirs = createAsyncThunk(
    "projects/fetchOwnerChoirs",
    async (directorEmail: string, {rejectWithValue}): Promise<Choir[] | any> => {
        try {
            return await ChoirService.getChoirs({
                director_email: directorEmail,
                page: 1,
                limit: 50,
            });
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

const setPending = (state: ProjectsState) => {
    state.loading = true;
    state.error = null;
};

const setRejected = (state: ProjectsState, action: any) => {
    state.loading = false;
    state.error = action.payload || action.error?.message || 'Something went wrong';
};

const projectSlice = createSlice({
    name: "projects",
    initialState,
    reducers: {
        clearProjectState: (state) => {
            state.project = null;
            state.projectRoles = [];
            state.projectRoleInvites = [];
            state.projectApplications = [];
            state.projectApplication = null;
            state.projectMembers = [];
            state.projectAgreements = [];
            state.projectAgreement = null;
            state.workspace = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProjects.pending, setPending)
            .addCase(fetchProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.projects = action.payload || [];
            })
            .addCase(fetchProjects.rejected, setRejected)
            .addCase(fetchOwnerProjects.pending, setPending)
            .addCase(fetchOwnerProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.ownerProjects = action.payload || [];
            })
            .addCase(fetchOwnerProjects.rejected, setRejected)
            .addCase(fetchProject.pending, setPending)
            .addCase(fetchProject.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload || null;
                state.workspace = action.payload?.workspace || state.workspace;
            })
            .addCase(fetchProject.rejected, setRejected)
            .addCase(createProject.pending, setPending)
            .addCase(createProject.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload as Project;
                if (action.payload) {
                    state.ownerProjects = [action.payload, ...state.ownerProjects];
                }
            })
            .addCase(createProject.rejected, setRejected)
            .addCase(updateProject.pending, setPending)
            .addCase(updateProject.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload || null;
                state.ownerProjects = state.ownerProjects.map((project) => {
                    if (`${project.id}` === `${action.payload?.id}` || project.uuid === action.payload?.uuid) {
                        return {...project, ...action.payload};
                    }

                    return project;
                });
            })
            .addCase(updateProject.rejected, setRejected)
            .addCase(fetchProjectRoles.pending, setPending)
            .addCase(fetchProjectRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoles = action.payload || [];
            })
            .addCase(fetchProjectRoles.rejected, setRejected)
            .addCase(createProjectRole.pending, setPending)
            .addCase(createProjectRole.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoles = action.payload?.roles || [];
            })
            .addCase(createProjectRole.rejected, setRejected)
            .addCase(updateProjectRole.pending, setPending)
            .addCase(updateProjectRole.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoles = action.payload?.roles || [];
            })
            .addCase(updateProjectRole.rejected, setRejected)
            .addCase(deleteProjectRole.pending, setPending)
            .addCase(deleteProjectRole.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoles = action.payload || [];
            })
            .addCase(deleteProjectRole.rejected, setRejected)
            .addCase(fetchProjectApplications.pending, setPending)
            .addCase(fetchProjectApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.projectApplications = action.payload || [];
            })
            .addCase(fetchProjectApplications.rejected, setRejected)
            .addCase(fetchProjectApplication.pending, setPending)
            .addCase(fetchProjectApplication.fulfilled, (state, action) => {
                state.loading = false;
                state.projectApplication = action.payload || null;
            })
            .addCase(fetchProjectApplication.rejected, setRejected)
            .addCase(fetchMyProjectApplications.pending, setPending)
            .addCase(fetchMyProjectApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.myApplications = action.payload || [];
            })
            .addCase(fetchMyProjectApplications.rejected, setRejected)
            .addCase(applyToProjectRole.pending, setPending)
            .addCase(applyToProjectRole.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.myApplications = [action.payload, ...state.myApplications];
                }
            })
            .addCase(applyToProjectRole.rejected, setRejected)
            .addCase(reviewProjectApplication.pending, setPending)
            .addCase(reviewProjectApplication.fulfilled, (state, action) => {
                state.loading = false;
                state.projectApplications = state.projectApplications.map((application) => {
                    if (`${application.id}` === `${action.payload?.id}`) {
                        return {...application, ...action.payload};
                    }
                    return application;
                });
            })
            .addCase(reviewProjectApplication.rejected, setRejected)
            .addCase(fetchProjectMembers.pending, setPending)
            .addCase(fetchProjectMembers.fulfilled, (state, action) => {
                state.loading = false;
                state.projectMembers = action.payload || [];
            })
            .addCase(fetchProjectMembers.rejected, setRejected)
            .addCase(fetchProjectAgreements.pending, setPending)
            .addCase(fetchProjectAgreements.fulfilled, (state, action) => {
                state.loading = false;
                state.projectAgreements = action.payload || [];
            })
            .addCase(fetchProjectAgreements.rejected, setRejected)
            .addCase(createProjectAgreement.pending, setPending)
            .addCase(createProjectAgreement.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.projectAgreements = [
                        action.payload,
                        ...state.projectAgreements.filter((agreement) => `${agreement.id}` !== `${action.payload.id}`),
                    ];
                }
            })
            .addCase(createProjectAgreement.rejected, setRejected)
            .addCase(fetchProjectAgreement.pending, setPending)
            .addCase(fetchProjectAgreement.fulfilled, (state, action) => {
                state.loading = false;
                state.projectAgreement = action.payload || null;
                if (action.payload) {
                    state.projectAgreements = [
                        action.payload,
                        ...state.projectAgreements.filter((agreement) => `${agreement.id}` !== `${action.payload.id}`),
                    ];
                }
            })
            .addCase(fetchProjectAgreement.rejected, setRejected)
            .addCase(fundProjectAgreement.pending, setPending)
            .addCase(fundProjectAgreement.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fundProjectAgreement.rejected, setRejected)
            .addCase(fundProjectAgreements.pending, setPending)
            .addCase(fundProjectAgreements.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fundProjectAgreements.rejected, setRejected)
            .addCase(releaseProjectAgreement.pending, setPending)
            .addCase(releaseProjectAgreement.fulfilled, (state, action) => {
                state.loading = false;
                state.projectAgreement = action.payload || state.projectAgreement;
                if (action.payload) {
                    state.projectAgreements = [
                        action.payload,
                        ...state.projectAgreements.filter((agreement) => `${agreement.id}` !== `${action.payload.id}`),
                    ];
                }
            })
            .addCase(releaseProjectAgreement.rejected, setRejected)
            .addCase(refundProjectAgreement.pending, setPending)
            .addCase(refundProjectAgreement.fulfilled, (state, action) => {
                state.loading = false;
                state.projectAgreement = action.payload || state.projectAgreement;
                if (action.payload) {
                    state.projectAgreements = [
                        action.payload,
                        ...state.projectAgreements.filter((agreement) => `${agreement.id}` !== `${action.payload.id}`),
                    ];
                }
            })
            .addCase(refundProjectAgreement.rejected, setRejected)
            .addCase(requestProjectAgreementWithdrawal.pending, setPending)
            .addCase(requestProjectAgreementWithdrawal.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(requestProjectAgreementWithdrawal.rejected, setRejected)
            .addCase(createProjectRoleInvite.pending, setPending)
            .addCase(createProjectRoleInvite.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(createProjectRoleInvite.rejected, setRejected)
            .addCase(fetchProjectRoleInvites.pending, setPending)
            .addCase(fetchProjectRoleInvites.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoleInvites = action.payload || [];
            })
            .addCase(fetchProjectRoleInvites.rejected, setRejected)
            .addCase(updateProjectRoleInvite.pending, setPending)
            .addCase(updateProjectRoleInvite.fulfilled, (state, action) => {
                state.loading = false;
                state.projectRoleInvites = state.projectRoleInvites.map((invite) => {
                    if (`${invite.id}` === `${action.payload?.id}`) {
                        return {...invite, ...action.payload};
                    }

                    return invite;
                });
            })
            .addCase(updateProjectRoleInvite.rejected, setRejected)
            .addCase(fetchMyProjectInvites.pending, setPending)
            .addCase(fetchMyProjectInvites.fulfilled, (state, action) => {
                state.loading = false;
                state.myInvites = action.payload || [];
            })
            .addCase(fetchMyProjectInvites.rejected, setRejected)
            .addCase(linkProjectChoirWorkspace.pending, setPending)
            .addCase(linkProjectChoirWorkspace.fulfilled, (state, action) => {
                state.loading = false;
                state.workspace = action.payload || null;
            })
            .addCase(linkProjectChoirWorkspace.rejected, setRejected)
            .addCase(syncProjectChoirWorkspace.pending, setPending)
            .addCase(syncProjectChoirWorkspace.fulfilled, (state, action) => {
                state.loading = false;
                const syncedCount = Number(action.payload?.synced_count || action.payload?.synced_members_count || 0);
                if (state.workspace && syncedCount > 0) {
                    state.workspace = {
                        ...state.workspace,
                        synced_members_count: Number(state.workspace.synced_members_count || 0) + syncedCount,
                    };
                }
            })
            .addCase(syncProjectChoirWorkspace.rejected, setRejected)
            .addCase(fetchOwnerChoirs.pending, setPending)
            .addCase(fetchOwnerChoirs.fulfilled, (state, action) => {
                state.loading = false;
                state.ownerChoirs = action.payload || [];
            })
            .addCase(fetchOwnerChoirs.rejected, setRejected);
    }
});

export const {clearProjectState} = projectSlice.actions;
export default projectSlice.reducer;
